/**
 * Volume mapping utilities for hearing test
 *
 * Converts user interface volume levels (1-12) to audio gain values
 * with frequency-specific calibration based on audiometric standards.
 */

// Frequency-specific maximum dB values (calibrated for headphones)
const FREQUENCY_MAX_DB: Record<number, number> = {
  500: 88.5562093,
  1000: 91.4321862,
  2000: 93.4206979,
  4000: 96.8948068,
};

// User volume level to dB reduction mapping
// Level 1 = minimum volume (0 dB reduction) - hardest to hear (smallest button)
// Level 12 = max volume (80 dB reduction) - easiest to hear (largest button)
const LEVEL_TO_DB_REDUCTION: readonly number[] = [
  0, 10, 15, 20, 30, 35, 40, 50, 55, 60, 70, 80,
] as const;

/**
 * Converts a user volume level (1-12) to a linear gain value (0-1)
 * for Web Audio API GainNode
 *
 * Formula from original: reducedLevel = maxDb - minDb (where minDb = level reduction)
 * Then: gain = 10^(-reducedLevel / 20)
 */
export function calculateGainValue(userLevel: number, frequency: number): number {
  const maxDb = FREQUENCY_MAX_DB[frequency];
  const minDb = LEVEL_TO_DB_REDUCTION[userLevel - 1];
  const reducedLevel = maxDb - minDb;

  return Math.pow(10, -reducedLevel / 20);
}
